/**
 * Notification Context
 * 
 * Global notification state management:
 * - Subscribes to Firestore `controllers/{id}/notifications` collection (real-time)
 * - Tracks unread count for sidebar badge
 * - Manages toast queue for displaying real-time notifications
 * - Provides acknowledge/dismiss actions
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { collection, query, orderBy, limit, onSnapshot, doc, updateDoc, where, writeBatch, Timestamp } from 'firebase/firestore';
import { db, CONTROLLER_ID } from '../firebase';
import { requestNotificationPermission, showLocalNotification } from '../utils/pwa';
import {
  shouldPlaySound,
  shouldShowBrowserNotification,
  shouldShowToast,
  subscribeDevicePrefs,
  getDevicePrefs,
} from '../utils/devicePrefs';
import { isRoomAlertsDeliveryEnabled } from '../utils/alertSettings';
import { formatOperatorAlertText } from '../utils/decisionDisplay';
import { BREAKPOINT_LG } from '../hooks/useViewport';
import type { AlertThresholdsConfig } from '../types/alerts';

// ============================================================================
// Types
// ============================================================================

export interface AppNotification {
  id: string;
  title: string;
  message: string;
  type: 'climate' | 'device' | 'ai' | 'system';
  severity: 'info' | 'warning' | 'critical';
  priority: string;
  roomId?: string;
  deviceId?: string;
  source: string;
  read: boolean;
  acknowledged: boolean;
  timestamp: Date;
  data?: Record<string, unknown>;
}

export interface Toast {
  id: string;
  notification: AppNotification;
  createdAt: number;
  dismissAt: number; // auto-dismiss timestamp
  dismissed: boolean;
}

interface NotificationContextValue {
  notifications: AppNotification[];
  unreadCount: number;
  activeAlertCount: number;
  toasts: Toast[];
  acknowledgeNotification: (id: string) => Promise<void>;
  acknowledgeNotifications: (ids: string[]) => Promise<void>;
  acknowledgeAllActive: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  dismissToast: (id: string) => void;
}

// ============================================================================
// Context
// ============================================================================

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function useNotifications(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return ctx;
}

// ============================================================================
// Provider
// ============================================================================

const TOAST_DURATIONS: Record<string, number> = {
  info: 8000,
  warning: 10000,
  critical: 20000,
};

const MAX_TOASTS = 3;

/** Ignore events older than this when deciding to toast / beep / OS-notify.
 *  Prevents stale bursts after a sleeping tab reconnects or alert settings change. */
const DELIVERY_FRESHNESS_MS = 2 * 60 * 1000;

function isDesktopViewport(): boolean {
  return typeof window !== 'undefined'
    && window.matchMedia(`(min-width: ${BREAKPOINT_LG}px)`).matches;
}

function isFreshNotification(n: AppNotification, now: number): boolean {
  return now - n.timestamp.getTime() <= DELIVERY_FRESHNESS_MS;
}

// Audio alert for critical notifications.
// Exported so the Settings "play test sound" button uses the exact same beep.
let audioContext: AudioContext | null = null;

export function playAlertSound(severity: 'info' | 'warning' | 'critical') {
  if (severity !== 'critical' && severity !== 'warning') return;
  
  try {
    if (!audioContext) {
      audioContext = new AudioContext();
    }
    
    // Resume context if suspended (browser policy)
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }
    
    const now = audioContext.currentTime;
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    if (severity === 'critical') {
      // Urgent two-tone alert
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, now);       // A5
      oscillator.frequency.setValueAtTime(660, now + 0.15); // E5
      oscillator.frequency.setValueAtTime(880, now + 0.3);  // A5
      gainNode.gain.setValueAtTime(0.15, now);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.45);
      oscillator.start(now);
      oscillator.stop(now + 0.45);
    } else {
      // Gentle single tone for warning
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(523, now); // C5
      gainNode.gain.setValueAtTime(0.08, now);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
      oscillator.start(now);
      oscillator.stop(now + 0.3);
    }
  } catch {
    // Audio not available -- silently ignore
  }
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [roomOverrides, setRoomOverrides] = useState<AlertThresholdsConfig['roomOverrides']>({});
  const seenIdsRef = useRef<Set<string>>(new Set());
  const initialLoadRef = useRef(true);

  useEffect(() => {
    const settingsRef = doc(db, 'ai_controller_settings', CONTROLLER_ID);
    return onSnapshot(settingsRef, (snapshot) => {
      const data = snapshot.data();
      setAlertsEnabled(data?.alertsEnabled ?? true);
      // Content-compare: every settings snapshot creates a fresh object, and
      // roomOverrides is a dep of the notifications listener effect below.
      // Only publish a new reference when the content actually changed, so
      // unrelated settings writes don't tear down and resubscribe the
      // notifications listener (whose delivery side effects are load-bearing).
      const nextOverrides = (data?.alertThresholds as AlertThresholdsConfig | undefined)?.roomOverrides ?? {};
      setRoomOverrides(prev =>
        JSON.stringify(prev) === JSON.stringify(nextOverrides) ? prev : nextOverrides
      );
    }, (error) => {
      console.warn('Notification settings subscription error:', error);
    });
  }, []);

  // Subscribe to Firestore notifications (real-time)
  useEffect(() => {
    const notifRef = collection(db, 'controllers', CONTROLLER_ID, 'notifications');
    // Get notifications from the last 7 days
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const q = query(
      notifRef,
      where('createdAt', '>', Timestamp.fromDate(sevenDaysAgo)),
      orderBy('createdAt', 'desc'),
      limit(100)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifs: AppNotification[] = snapshot.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          // Display-layer humanizer (human-readable-ui rule): already-persisted
          // rows may carry machine keys the controller has since stopped writing.
          title: formatOperatorAlertText(data.title || 'Notification'),
          message: formatOperatorAlertText(data.message || ''),
          type: data.type || 'system',
          severity: data.severity || 'info',
          priority: data.priority || 'normal',
          roomId: data.roomId || undefined,
          deviceId: data.deviceId || undefined,
          source: data.source || 'system',
          read: data.read || false,
          acknowledged: data.acknowledged || false,
          timestamp: data.timestamp ? new Date(data.timestamp) : (data.createdAt?.toDate?.() || new Date()),
          data: data.data,
        };
      }).filter((notif) => {
        const roomId = notif.roomId ?? (notif.data?.roomId as string | undefined);
        return isRoomAlertsDeliveryEnabled(roomId, alertsEnabled, roomOverrides);
      });

      setNotifications(notifs);

      if (initialLoadRef.current) {
        // On initial load, just mark all existing IDs as seen
        notifs.forEach(n => seenIdsRef.current.add(n.id));
        initialLoadRef.current = false;
      } else {
        // Compute the genuinely NEW notifications once, then independently
        // derive the three delivery surfaces. Sound and OS notifications must
        // NOT depend on toast creation: toasts are info-only (warning/critical
        // are owned by the AlertsBanner), but sound covers warning/critical.
        const newNotifs = notifs.filter(n => !seenIdsRef.current.has(n.id) && !n.acknowledged);
        newNotifs.forEach(n => seenIdsRef.current.add(n.id));

        if (newNotifs.length > 0) {
          const now = Date.now();
          // Skip stale events (tab sleep reconnect, settings re-subscribe).
          const freshNotifs = newNotifs.filter(n => isFreshNotification(n, now));

          // (a) Toasts: info severity only — one surface per severity.
          // Desktop viewports only; gated by device-local toastsEnabled.
          if (freshNotifs.length > 0 && shouldShowToast() && isDesktopViewport()) {
            const newToasts: Toast[] = freshNotifs
              .filter(n => n.severity === 'info')
              .map(n => ({
                id: `toast-${n.id}`,
                notification: n,
                createdAt: now,
                dismissAt: now + (TOAST_DURATIONS[n.severity] || TOAST_DURATIONS.info),
                dismissed: false,
              }));
            if (newToasts.length > 0) {
              setToasts(prev => [...newToasts, ...prev].filter(t => !t.dismissed).slice(0, MAX_TOASTS + 2));
            }
          }

          // (b) Sound: most severe fresh notification, gated by device prefs.
          if (freshNotifs.length > 0) {
            const rank = { critical: 3, warning: 2, info: 1 } as const;
            const mostSevere = freshNotifs.reduce((max, n) =>
              (rank[n.severity] || 0) > (rank[max.severity] || 0) ? n : max,
            freshNotifs[0]);
            if (shouldPlaySound(mostSevere.severity)) {
              playAlertSound(mostSevere.severity);
            }
          }

          // (c) OS notification: warning/critical while the tab is hidden,
          // gated by device prefs. Fresh only — avoid overnight bursts.
          if (document.hidden && freshNotifs.length > 0) {
            for (const notif of freshNotifs) {
              if (shouldShowBrowserNotification(notif.severity)) {
                showLocalNotification(
                  notif.title,
                  {
                    body: notif.message,
                    tag: notif.id, // prevents duplicate OS notifications
                    requireInteraction: notif.severity === 'critical',
                  }
                ).catch(() => {}); // ignore if not permitted
              }
            }
          }
        }
      }
    }, (error) => {
      console.warn('Notification subscription error:', error);
    });

    return () => unsubscribe();
  }, [alertsEnabled, roomOverrides]);

  // Request notification permission on first interaction
  useEffect(() => {
    const requestPermission = () => {
      requestNotificationPermission().catch(() => {});
      // Only request once
      window.removeEventListener('click', requestPermission);
    };
    
    // Request permission on first user click (browsers require user gesture)
    if ('Notification' in window && Notification.permission === 'default') {
      window.addEventListener('click', requestPermission, { once: true });
      return () => window.removeEventListener('click', requestPermission);
    }
  }, []);

  // Clear open toasts immediately when the device-local mute flips off.
  useEffect(() => {
    const sync = () => {
      if (!getDevicePrefs().toastsEnabled) {
        setToasts([]);
      }
    };
    sync();
    return subscribeDevicePrefs(sync);
  }, []);

  // NOTE: toast auto-dismiss ticking lives in NotificationToastContainer (a
  // leaf) and calls dismissToast() only when a toast actually expires — a 1 s
  // interval here used to invalidate this context (and the whole tree) every
  // second. Dismissed toasts are pruned on the next toast insertion above.

  const applyAcknowledgedLocally = useCallback((ids: string[]) => {
    const idSet = new Set(ids);
    setNotifications(prev =>
      prev.map(n => (idSet.has(n.id) ? { ...n, acknowledged: true, read: true } : n)),
    );
    setToasts(prev =>
      prev.map(t => (idSet.has(t.notification.id) ? { ...t, dismissed: true } : t)),
    );
  }, []);

  const persistAcknowledged = useCallback(async (ids: string[]) => {
    // One batched commit instead of N sequential updates (Firestore caps a
    // batch at 500 writes; the inbox query is capped at 100 docs).
    try {
      const CHUNK = 450;
      for (let i = 0; i < ids.length; i += CHUNK) {
        const batch = writeBatch(db);
        for (const id of ids.slice(i, i + CHUNK)) {
          batch.update(doc(db, 'controllers', CONTROLLER_ID, 'notifications', id), {
            acknowledged: true,
            read: true,
          });
        }
        await batch.commit();
      }
    } catch (err) {
      console.warn('Failed to acknowledge notifications:', err);
    }
  }, []);

  // Acknowledge a single notification (optimistic UI)
  const acknowledgeNotification = useCallback(async (id: string) => {
    applyAcknowledgedLocally([id]);
    await persistAcknowledged([id]);
  }, [applyAcknowledgedLocally, persistAcknowledged]);

  // Acknowledge multiple notifications at once (for grouped banner chips)
  const acknowledgeNotifications = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return;
    applyAcknowledgedLocally(ids);
    await persistAcknowledged(ids);
  }, [applyAcknowledgedLocally, persistAcknowledged]);

  // Acknowledge every active warning/critical notification
  const acknowledgeAllActive = useCallback(async () => {
    const ids = notifications
      .filter(n => !n.acknowledged && (n.severity === 'warning' || n.severity === 'critical'))
      .map(n => n.id);
    if (ids.length === 0) return;
    applyAcknowledgedLocally(ids);
    await persistAcknowledged(ids);
  }, [notifications, applyAcknowledgedLocally, persistAcknowledged]);

  // Mark as read
  const markAsRead = useCallback(async (id: string) => {
    try {
      const notifRef = doc(db, 'controllers', CONTROLLER_ID, 'notifications', id);
      await updateDoc(notifRef, { read: true });
    } catch (err) {
      console.warn('Failed to mark notification as read:', err);
    }
  }, []);

  // Mark all as read (single batched commit)
  const markAllAsRead = useCallback(async () => {
    const unread = notifications.filter(n => !n.read);
    if (unread.length === 0) return;
    try {
      const batch = writeBatch(db);
      for (const n of unread) {
        batch.update(doc(db, 'controllers', CONTROLLER_ID, 'notifications', n.id), { read: true });
      }
      await batch.commit();
    } catch (err) {
      console.warn('Failed to mark all as read:', err);
    }
  }, [notifications]);

  // Dismiss a toast
  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, dismissed: true } : t));
  }, []);

  const value = useMemo<NotificationContextValue>(() => ({
    notifications,
    unreadCount: notifications.filter(n => !n.read && !n.acknowledged).length,
    activeAlertCount: notifications.filter(
      n => !n.acknowledged && (n.severity === 'warning' || n.severity === 'critical'),
    ).length,
    toasts: toasts.filter(t => !t.dismissed).slice(0, MAX_TOASTS),
    acknowledgeNotification,
    acknowledgeNotifications,
    acknowledgeAllActive,
    markAsRead,
    markAllAsRead,
    dismissToast,
  }), [
    notifications, toasts,
    acknowledgeNotification, acknowledgeNotifications, acknowledgeAllActive,
    markAsRead, markAllAsRead, dismissToast,
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export default NotificationProvider;
